import { useCallback } from "react";

import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import z from "zod";

import { SyncRequestClient } from "../stores/requestClient";
import { SearchParam, ServiceErrorCode, type SharingSpace } from "../types";
import { buildAdvancedTypePredicateFilter, buildTypePredicateFilter } from "../utils/typeUtils";
import { type UserID } from "./useAccount";
import {
  projectViewLinkSharingQueryKey,
  ProjectViewLinkSharingResponseData,
} from "./useProjectViewLink";
import useTranslation from "./useTranslation";

export const ProjectUserAssignableRoles = ["editor", "commenter", "viewer"] as const;
export type ProjectUserAssignableRole = (typeof ProjectUserAssignableRoles)[number];

export const useProjectUserRoleDisplayName = () => {
  const { t, m } = useTranslation();
  return useCallback(
    (role: ProjectUserAssignableRole) => {
      switch (role) {
        case "editor":
          return t(m.ProjectRole.Editor);
        case "commenter":
          return t(m.ProjectRole.Commenter);
        case "viewer":
          return t(m.ProjectRole.Viewer);
      }
    },
    [t, m]
  );
};

type ProjectUserBase = {
  email: string;
  createdAt: string;
};

export type ProjectMember = ProjectUserBase & {
  userId: UserID;
  name?: string;
  role: ProjectUserAssignableRole;
  isTeamUser: boolean;
};

export const ProjectInvitationID = z.string().brand("ProjectInvitationID");
export type ProjectInvitationID = z.infer<typeof ProjectInvitationID>;

export const ProjectAccessRequestID = z.string().brand("ProjectAccessRequestID");
export type ProjectAccessRequestID = z.infer<typeof ProjectAccessRequestID>;

export type ProjectInvitation = ProjectUserBase & {
  invitationId: ProjectInvitationID;
  role: ProjectUserAssignableRole;
};

export type ProjectAccessRequest = ProjectUserBase & {
  accessRequestId: ProjectAccessRequestID;
  name?: string;
  userId: UserID;
};

export type ProjectUser = ProjectMember | ProjectInvitation | ProjectAccessRequest;

export type ProjectSharingData = {
  users: ProjectUser[];
  space?: SharingSpace;
};

export type ProjectInvitationRequestPayload = {
  emails: string[];
  role: ProjectUserAssignableRole;
};

export type ProjectInvitationResponse = {
  invitedUsers: {
    email: string;
    role: ProjectUserAssignableRole;
    userId?: UserID;
    invitationId?: ProjectInvitationID;
    name?: string;
  }[];
  errors: {
    email: string;
    code: string;
    message: string;
  }[];
};

type GetProjectInvitationResponse = {
  email?: string;
  action: "view" | "accept" | "signup" | "login";
};

export const projectSharingQueryKey = ["projectSharingQueryKey"];
export const projectInvitationQueryKey = ["projectInvitationQueryKey"];

const projectViewLinkSharingQueriesForProject = (projectId?: string) => ({
  queryKey: [...projectViewLinkSharingQueryKey, projectId],
});

const invalidateProjectViewLinkSharingQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId?: string
) => {
  if (!projectId) {
    return;
  }

  queryClient.invalidateQueries({
    ...projectViewLinkSharingQueriesForProject(projectId),
    refetchType: "active",
  });
};

const setProjectViewLinkSharingProjectUsers = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | undefined,
  updater: (projectUsers: ProjectMember[]) => ProjectMember[]
) => {
  if (!projectId) {
    return;
  }

  queryClient.setQueriesData(
    projectViewLinkSharingQueriesForProject(projectId),
    (oldData: ProjectViewLinkSharingResponseData | undefined) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        projectUsers: updater(oldData.projectUsers),
      };
    }
  );
};

export const isProjectInvitation = buildTypePredicateFilter<ProjectUser, ProjectInvitation>(
  "invitationId"
);

export const isProjectMember = buildAdvancedTypePredicateFilter<ProjectUser, ProjectMember>({
  required: ["userId", "role"],
});

export const isProjectAccessRequest = buildTypePredicateFilter<ProjectUser, ProjectAccessRequest>(
  "accessRequestId"
);

export const useProjectInvitation = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  const enabled = Boolean(invitationHash);

  const query = useQuery({
    queryKey: [...projectInvitationQueryKey],
    queryFn: async () => {
      const request = isLoggedIn()
        ? SyncRequestClient.authenticatedRequest
        : SyncRequestClient.publicRequest;

      const response = await request({
        method: "GET",
        url: `/web/projects/${projectId}/invitations-by-hash/${invitationHash}`,
      });
      return response.json<GetProjectInvitationResponse>();
    },
    enabled,
  });

  return { ...query, enabled };
};

export const useMutateAcceptProjectInvitation = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ emailToken }: { emailToken?: string | null } = {}) => {
      try {
        return await SyncRequestClient.authenticatedRequest({
          method: "POST",
          url: `/web/projects/${projectId}/invitations-by-hash/${invitationHash}/accept`,
          body: { ...(emailToken && { emailToken }) },
        });
      } catch (error) {
        // Accept invitation is best effort.
        // Not found could mean you can view the project or could mean you can't.
        // Up to the project loading to decide and show the proper error screen.
        if (
          error instanceof ServiceError &&
          (error.hasCode(ServiceErrorCode.NotFound) ||
            error.hasCode(ServiceErrorCode.InvitationNotFound))
        ) {
          return;
        }
        throw error;
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        ...projectViewLinkSharingQueriesForProject(projectId),
        refetchType: "active",
      });
    },
  });
};

export const useProjectSharing = () => {
  const { projectId } = useParams();
  return useQuery({
    queryKey: [...projectSharingQueryKey, projectId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/projects/${projectId}/sharing`,
      });
      return response.json<ProjectSharingData>();
    },
    select: (data) => {
      const members: ProjectMember[] = data.users.filter(isProjectMember);
      const invitations: ProjectInvitation[] = data.users.filter(isProjectInvitation);
      const accessRequests: ProjectAccessRequest[] = data.users.filter(isProjectAccessRequest);

      return { ...data, members, invitations, accessRequests };
    },
    staleTime: 20 * 60 * 1000,
    enabled: isLoggedIn() && Boolean(projectId),
  });
};

export const useCreateProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ProjectInvitationResponse, variables: ProjectInvitationRequestPayload) => void;
  onError?: (error: Error, variables: ProjectInvitationRequestPayload) => void;
} = {}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...projectSharingQueryKey, projectId],
    mutationFn: async (invitations: ProjectInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/invitations`,
        body: invitations,
      });
      return response.json<ProjectInvitationResponse>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            users: [...oldData.users, ...data.invitedUsers],
          };
        }
      );
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useUpdateProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      projectInvitationId: ProjectInvitationID;
      role: ProjectUserAssignableRole;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/projects/${projectId}/invitations/${data.projectInvitationId}`,
        body: {
          role: data.role,
        },
      });
      return data;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);

      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as ProjectSharingData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectInvitation(user) && user.invitationId === variables.projectInvitationId) {
              return {
                ...user,
                role: variables.role,
              };
            }
            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }
      onError?.(error);
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      invalidateProjectViewLinkSharingQueries(queryClient, projectId);
    },
  });
};

export const useDeleteProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ProjectInvitationID, variables: { invitationId: ProjectInvitationID }) => void;
  onError?: (error: Error, variables: { invitationId: ProjectInvitationID }) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: ProjectInvitationID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/invitations/${invitationId}`,
      });
      return invitationId;
    },
    onMutate: async ({ invitationId }) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);

      removeInvitationFromCache(invitationId);

      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }

      LogSentryException({ messagePrefix: "Failed to delete project invitation", error });
      onError?.(error, variables);
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      invalidateProjectViewLinkSharingQueries(queryClient, projectId);
    },
  });

  const removeInvitationFromCache = (invitationId: ProjectInvitationID) => {
    queryClient.setQueryData(
      [...projectSharingQueryKey, projectId],
      (oldData: ProjectSharingData | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) => !(isProjectInvitation(user) && user.invitationId === invitationId)
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeInvitationFromCache,
  };
};

export const useUpdateProjectUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: UserID; role: ProjectUserAssignableRole }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/projects/${projectId}/users/${userId}`,
        body: {
          role: role,
        },
      });
      return { userId, role };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });
      await queryClient.cancelQueries(projectViewLinkSharingQueriesForProject(projectId));

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);
      const previousViewLinkSharingData = queryClient.getQueriesData<
        ProjectViewLinkSharingResponseData | undefined
      >(projectViewLinkSharingQueriesForProject(projectId));

      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as ProjectSharingData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectMember(user) && user.userId === variables.userId) {
              return {
                ...user,
                role: variables.role,
              };
            }
            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      setProjectViewLinkSharingProjectUsers(queryClient, projectId, (projectUsers) =>
        projectUsers.map((user) => {
          if (user.userId !== variables.userId) {
            return user;
          }

          return {
            ...user,
            role: variables.role,
          };
        })
      );

      return { previousData, previousViewLinkSharingData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }

      context?.previousViewLinkSharingData?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      onError?.(error);
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      invalidateProjectViewLinkSharingQueries(queryClient, projectId);
    },
  });
};

export const useDeleteProjectUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: UserID, variables: { userId: UserID }) => void;
  onError?: (error: Error, variables: { userId: UserID }) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ userId }: { userId: UserID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/users/${userId}`,
      });
      return userId;
    },
    onMutate: async ({ userId }) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });
      await queryClient.cancelQueries(projectViewLinkSharingQueriesForProject(projectId));

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);
      const previousViewLinkSharingData = queryClient.getQueriesData<
        ProjectViewLinkSharingResponseData | undefined
      >(projectViewLinkSharingQueriesForProject(projectId));

      removeUserFromCache(userId);

      return { previousData, previousViewLinkSharingData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }

      context?.previousViewLinkSharingData?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      LogSentryException({ messagePrefix: "Failed to delete project user", error });
      onError?.(error, variables);
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      invalidateProjectViewLinkSharingQueries(queryClient, projectId);
    },
  });

  const removeUserFromCache = (userId: UserID) => {
    queryClient.setQueryData(
      [...projectSharingQueryKey, projectId],
      (oldData: ProjectSharingData | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) =>
              !(
                (isProjectMember(user) && user.userId === userId) ||
                (isProjectAccessRequest(user) && user.userId === userId)
              )
          ),
        };
      }
    );

    setProjectViewLinkSharingProjectUsers(queryClient, projectId, (projectUsers) =>
      projectUsers.filter(({ userId: projectUserId }) => projectUserId !== userId)
    );
  };

  return {
    ...mutation,
    removeUserFromCache,
  };
};

export const useRequestProjectAccess = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  return useMutation({
    mutationFn: async () => {
      await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/access-requests`,
      });
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      LogSentryException({ messagePrefix: "Failed to access request", error });
      onError?.(error);
    },
    retry: 1,
    retryDelay: 500,
  });
};

export const useAcceptProjectAccessRequest = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectAccessRequestId,
      role,
    }: {
      projectAccessRequestId: ProjectAccessRequestID;
      role: ProjectUserAssignableRole;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/access-requests/${projectAccessRequestId}`,
        body: {
          role: role,
        },
      });
      return { projectAccessRequestId, role };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);

      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            users: oldData.users.map((user) => {
              if (
                isProjectAccessRequest(user) &&
                user.accessRequestId === variables.projectAccessRequestId
              ) {
                const { accessRequestId: _drop, ...rest } = user;
                return {
                  ...rest,
                  role: variables.role,
                };
              }

              return user;
            }),
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }

      LogSentryException({ messagePrefix: "Failed to accept project request", error });
      onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      invalidateProjectViewLinkSharingQueries(queryClient, projectId);
    },
  });
};

export const useRejectProjectAccessRequest = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectAccessRequestId,
    }: {
      projectAccessRequestId: ProjectAccessRequestID;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/access-requests/${projectAccessRequestId}`,
      });
      return { projectAccessRequestId };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);

      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            users: oldData.users.filter(
              (user) =>
                !(
                  isProjectAccessRequest(user) &&
                  user.accessRequestId === variables.projectAccessRequestId
                )
            ),
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }

      LogSentryException({ messagePrefix: "Failed to reject project access request", error });
      onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...projectSharingQueryKey, projectId],
        refetchType: "active",
      });
      invalidateProjectViewLinkSharingQueries(queryClient, projectId);
    },
  });
};
